import type { Element, ElementContent, Root, RootContent } from "hast";
import { normalizeLinkHref } from "./normalize-link-href";
import { isAllowedIframeSrc } from "./iframe-host-allowlist";

/**
 * rehype plugin: canonicalize legacy rich-text before allowlisting.
 *
 * Sanitization (see `canonical-schema`) can only *drop* — it can't convert or
 * unwrap while preserving text. These transforms run first so the sanitizer
 * only ever sees tags to keep or remove:
 *
 *   h4,h5,h6            -> <p><strong>…</strong></p>   (demote heading, keep emphasis)
 *   blockquote, pre     -> <p><em>…</em></p>           (demote block, keep text)
 *   code (inline)       -> <em>…</em>
 *   hr                  -> <br><br>
 *   div, font           -> unwrapped (children preserved)
 *   embed               -> removed (subtree dropped)
 *   a[href]             -> href normalized (bare host gets https://, etc.)
 *   iframe              -> host-allowlisted, then hardened (see below)
 *
 * `iframe` is kept by the tag allowlist so legacy embeds (e.g. YouTube, published
 * Google Docs) render as view-only legacy content, the same as `img` — but only
 * from a trusted embed host. Every iframe passes two gates here, in order:
 *
 *   1. Host allowlist (`iframe-host-allowlist`): the src must resolve to a
 *      trusted third-party embed host, else the iframe is dropped subtree-and-all
 *      (like `<embed>`). This removes the same-origin sandbox-escape vector — a
 *      same-origin src can never be on a list of third-party hosts.
 *   2. Hardening: a restrictive `sandbox` (critically WITHOUT
 *      `allow-top-navigation*`, so a framed page can't redirect the tab it's
 *      embedded in — the phishing vector on customer-facing surfaces) and a
 *      privacy-preserving `referrerpolicy` are forced on. Forced (not merged) so
 *      legacy/author-supplied sandbox values can't loosen it.
 *
 * Block containers demoted to inline (blockquote/pre) have their block children
 * flattened to phrasing content so the result stays valid (no <p> inside <em>).
 */

function isElement(node: RootContent): node is Element {
  return node.type === "element";
}

function element(tagName: string, children: ElementContent[] = []): Element {
  return { type: "element", tagName, properties: {}, children };
}

const lineBreak = (): Element => element("br");

/**
 * Flatten flow content (which may include block <p>s) into phrasing content so
 * it can live inside an inline <em>. Each paragraph's children are lifted, with
 * a <br> inserted between consecutive paragraphs to preserve the line break.
 */
function toPhrasing(nodes: ElementContent[]): ElementContent[] {
  const out: ElementContent[] = [];
  for (const node of nodes) {
    if (isElement(node) && node.tagName === "p") {
      if (out.length > 0) out.push(lineBreak());
      out.push(...node.children);
    } else {
      out.push(node);
    }
  }
  return out;
}

function canonicalizeNodes(nodes: ElementContent[]): ElementContent[] {
  const out: ElementContent[] = [];

  for (const node of nodes) {
    if (!isElement(node)) {
      out.push(node);
      continue;
    }

    // Depth-first: children are already canonical before the parent is handled.
    const children = canonicalizeNodes(node.children);

    switch (node.tagName) {
      case "embed":
        // Drop entirely — legacy Flash-era embeds are unsupported. (Iframes are
        // kept: they pass through to the allowlist as view-only legacy media.)
        break;
      case "iframe": {
        // Host allowlist gate — runs BEFORE hardening. Only iframes whose src
        // resolves to a trusted embed host (see `iframe-host-allowlist`) survive;
        // anything else — an empty/relative/unparseable src, or an untrusted host
        // including one same-origin with the app — is dropped subtree-and-all,
        // like <embed>. This is what removes the same-origin sandbox-escape
        // vector: a frame can only load from a third-party host we vouch for, so
        // it can never remove its own sandbox by being same-origin with the app.
        if (!isAllowedIframeSrc(node.properties?.src)) break;
        // Harden the surviving (view-only) iframe. Force a restrictive sandbox —
        // notably WITHOUT allow-top-navigation*, so a framed page can't redirect
        // the customer's tab (the phishing vector) — plus a privacy-preserving
        // referrer policy. Forced, not merged, so legacy/author sandbox can't
        // loosen it.
        out.push({
          ...node,
          properties: {
            ...node.properties,
            sandbox: [
              "allow-scripts",
              "allow-same-origin",
              "allow-popups",
              "allow-forms",
              "allow-presentation",
            ],
            referrerPolicy: "strict-origin-when-cross-origin",
          },
          children,
        });
        break;
      }
      case "div":
      case "font":
        // Strip the tag, preserve its content.
        out.push(...children);
        break;
      case "hr":
        out.push(lineBreak(), lineBreak());
        break;
      case "h4":
      case "h5":
      case "h6":
        out.push(element("p", [element("strong", children)]));
        break;
      case "blockquote":
      case "pre":
        out.push(element("p", [element("em", toPhrasing(children))]));
        break;
      case "code":
        // Inline <code> demotes to <em>. (A <pre><code>…</code></pre> is handled
        // by the <pre> case above, which wraps this <em> in <p><em>.)
        out.push(element("em", children));
        break;
      case "a": {
        // Repair the href before the allowlist sees it: a bare host stored by
        // an editor that didn't normalize (`example.com`) is a relative link
        // that resolves against the current page — this makes it absolute
        // (`https://example.com`). Runs at every seam (load/persist/render), so
        // legacy links are fixed without a data migration. A missing/non-string
        // href is left alone for the sanitizer to handle.
        const { href } = node.properties ?? {};
        const properties =
          typeof href === "string"
            ? { ...node.properties, href: normalizeLinkHref(href) }
            : node.properties;
        out.push({ ...node, properties, children });
        break;
      }
      default:
        out.push({ ...node, children });
    }
  }

  return out;
}

export function rehypeCanonicalize() {
  return (tree: Root): void => {
    tree.children = canonicalizeNodes(
      tree.children as ElementContent[],
    ) as RootContent[];
  };
}
